// src/pages/ClaimOrder.js
import React, { useEffect, useState } from 'react';
import { collection, getDocs, updateDoc, doc, addDoc, query, orderBy, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';

export default function ClaimOrder() {
  const { currentUser, userProfile } = useAuth();
  const isAdmin = userProfile?.role === 'admin';
  const [orders, setOrders] = useState([]);
  const [reps, setReps] = useState([]);
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [claiming, setClaiming] = useState(null);
  const [success, setSuccess] = useState('');

  useEffect(() => {
    async function load() {
      const [ordSnap, repSnap, claimSnap] = await Promise.all([
        getDocs(query(collection(db, 'orders'), orderBy('createdAt', 'desc'))),
        getDocs(collection(db, 'users')),
        getDocs(collection(db, 'orderClaims')),
      ]);
      setOrders(ordSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setReps(repSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setClaims(claimSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }
    load();
  }, []);

  // Unassigned orders = no agent code or repId not matched to a real rep
  const unassigned = orders.filter(o => {
    if (!o.agentCode && !o.repId) return true;
    if (!o.agentCode && o.repName?.toLowerCase().includes('generic')) return true;
    if (o.repId === null || o.repId === undefined) return true;
    return false;
  });

  const filtered = unassigned.filter(o => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (o.customerName || '').toLowerCase().includes(q) ||
      (o.workOrderNumber || '').toLowerCase().includes(q) ||
      (o.accountNumber || '').toLowerCase().includes(q) ||
      (o.repName || '').toLowerCase().includes(q) ||
      (o.date || '').includes(q);
  });

  // Claims for current user (reps see their own, admins see all)
  const myClaims = isAdmin
    ? claims.filter(c => c.status === 'pending')
    : claims.filter(c => c.claimedBy === currentUser.uid);

  async function submitClaim(order) {
    setClaiming(order.id);
    setSuccess('');
    try {
      const docRef = await addDoc(collection(db, 'orderClaims'), {
        orderId: order.id,
        customerName: order.customerName || '',
        workOrderNumber: order.workOrderNumber || '',
        productName: order.productName || '',
        date: order.date || '',
        claimedBy: currentUser.uid,
        claimedByName: userProfile?.name || '',
        claimedByCode: userProfile?.agentCode || '',
        status: 'pending',
        createdAt: serverTimestamp(),
      });
      setClaims(prev => [...prev, {
        id: docRef.id,
        orderId: order.id,
        claimedBy: currentUser.uid,
        claimedByName: userProfile?.name || '',
        status: 'pending',
      }]);
      setSuccess(`Claim submitted for ${order.customerName}`);
    } catch (err) {
      alert('Error: ' + err.message);
    }
    setClaiming(null);
  }

  async function approveClaim(claim) {
    try {
      // Update the order with the claiming rep's info
      const rep = reps.find(r => r.id === claim.claimedBy) ||
        reps.find(r => r.name === claim.claimedByName);
      if (!rep) { alert('Rep not found'); return; }

      await updateDoc(doc(db, 'orders', claim.orderId), {
        repId: rep.id,
        repName: rep.name,
        agentCode: rep.agentCode || '',
        compPlanId: rep.compPlanId || 'synergy_1_31_26',
      });
      await updateDoc(doc(db, 'orderClaims', claim.id), { status: 'approved' });

      setOrders(prev => prev.map(o => o.id === claim.orderId
        ? { ...o, repId: rep.id, repName: rep.name, agentCode: rep.agentCode || '' }
        : o
      ));
      setClaims(prev => prev.map(c => c.id === claim.id ? { ...c, status: 'approved' } : c));
    } catch (err) {
      alert('Error: ' + err.message);
    }
  }

  async function denyClaim(claim) {
    try {
      await updateDoc(doc(db, 'orderClaims', claim.id), { status: 'denied' });
      setClaims(prev => prev.map(c => c.id === claim.id ? { ...c, status: 'denied' } : c));
    } catch (err) {
      alert('Error: ' + err.message);
    }
  }

  function isAlreadyClaimed(orderId) {
    return claims.some(c => c.orderId === orderId && (c.status === 'pending' || c.status === 'approved'));
  }

  if (loading) return <div style={{ padding: 60, color: '#555' }}>Loading...</div>;

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <h1 style={S.title}>Claim order</h1>
          <p style={S.sub}>
            {isAdmin
              ? `${myClaims.length} pending claim${myClaims.length !== 1 ? 's' : ''} to review · ${unassigned.length} unassigned orders`
              : `Called in an order? Find it below and claim it so you get credit.`}
          </p>
        </div>
      </div>

      {/* Admin: pending claims */}
      {isAdmin && myClaims.length > 0 && (
        <div style={S.section}>
          <div style={S.sectionTitle}>Pending claims</div>
          <div style={{ border: '1px solid #1e1e2a', borderRadius: 10, overflow: 'hidden' }}>
            {myClaims.map(claim => {
              const order = orders.find(o => o.id === claim.orderId);
              return (
                <div key={claim.id} style={S.claimRow}>
                  <div style={{ flex: 1 }}>
                    <div style={S.claimName}>{claim.claimedByName}</div>
                    <div style={S.claimDetail}>
                      {order?.customerName || 'Unknown'} · {order?.productName || ''} · {order?.date || ''}
                    </div>
                    {claim.workOrderNumber && <div style={S.claimDetail}>WO# {claim.workOrderNumber}</div>}
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button style={S.approveBtn} onClick={() => approveClaim(claim)}>Approve</button>
                    <button style={S.denyBtn} onClick={() => denyClaim(claim)}>Deny</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {success && <div style={S.successMsg}>{success}</div>}

      {/* Search + unassigned orders */}
      <div style={S.section}>
        <div style={S.sectionTitle}>Unassigned orders ({unassigned.length})</div>
        <input
          style={S.input}
          placeholder="Search by customer name, work order #, account #, or date..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div style={{ marginTop: 12, border: '1px solid #1e1e2a', borderRadius: 10, overflow: 'hidden', maxHeight: 500, overflowY: 'auto' }}>
          {filtered.slice(0, 100).map(order => {
            const claimed = isAlreadyClaimed(order.id);
            const myClaim = claims.find(c => c.orderId === order.id && c.claimedBy === currentUser.uid);
            return (
              <div key={order.id} style={{ ...S.orderRow, opacity: claimed ? 0.5 : 1 }}>
                <div style={{ flex: 1 }}>
                  <div style={S.orderName}>{order.customerName || 'No name'}</div>
                  <div style={S.orderDetail}>
                    {order.productName || order.productId} · {order.date || 'No date'} · {order.address || ''}
                  </div>
                  <div style={S.orderDetail}>
                    {order.workOrderNumber && `WO# ${order.workOrderNumber} · `}
                    {order.accountNumber && `Acct# ${order.accountNumber} · `}
                    {order.repName || 'No agent'}
                  </div>
                </div>
                <div>
                  {myClaim ? (
                    <span style={{ ...S.statusBadge, background: myClaim.status === 'approved' ? '#1a2e24' : myClaim.status === 'denied' ? '#2a1515' : '#1a1a28', color: myClaim.status === 'approved' ? '#00d4a0' : myClaim.status === 'denied' ? '#ff6b6b' : '#f5a623' }}>
                      {myClaim.status === 'pending' ? 'Pending' : myClaim.status === 'approved' ? 'Approved' : 'Denied'}
                    </span>
                  ) : claimed ? (
                    <span style={{ ...S.statusBadge, background: '#1a1a28', color: '#666' }}>Claimed</span>
                  ) : (
                    <button
                      style={S.claimBtn}
                      disabled={claiming === order.id}
                      onClick={() => submitClaim(order)}
                    >
                      {claiming === order.id ? 'Claiming...' : 'Claim'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div style={{ padding: 32, textAlign: 'center', color: '#444', fontSize: 13 }}>
              {search ? 'No matching unassigned orders' : 'No unassigned orders'}
            </div>
          )}
          {filtered.length > 100 && (
            <div style={{ padding: 12, textAlign: 'center', color: '#555', fontSize: 12 }}>
              Showing 100 of {filtered.length} — use search to narrow down
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const S = {
  page: { padding: '20px 16px', color: '#fff', width: '100%', boxSizing: 'border-box' },
  header: { marginBottom: 20 },
  title: { fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: '-0.3px' },
  sub: { fontSize: 12, color: '#555', margin: '4px 0 0' },
  section: { marginBottom: 20 },
  sectionTitle: { fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 500, marginBottom: 8 },
  input: { width: '100%', maxWidth: 500, background: '#1a1a24', border: '1px solid #2a2a3a', borderRadius: 8, padding: '10px 14px', color: '#fff', fontSize: 13, outline: 'none', boxSizing: 'border-box' },
  orderRow: { display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: '1px solid #111', background: '#13131a' },
  orderName: { fontSize: 14, fontWeight: 500, color: '#fff' },
  orderDetail: { fontSize: 11, color: '#555', marginTop: 2 },
  claimBtn: { background: '#00d4a0', border: 'none', borderRadius: 7, padding: '7px 16px', color: '#000', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  claimRow: { display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: '1px solid #111', background: '#13131a' },
  claimName: { fontSize: 14, fontWeight: 500, color: '#fff' },
  claimDetail: { fontSize: 11, color: '#555', marginTop: 2 },
  approveBtn: { background: '#1a2e24', border: '1px solid #2a5a3a', borderRadius: 7, padding: '6px 14px', color: '#00d4a0', fontSize: 12, fontWeight: 500, cursor: 'pointer' },
  denyBtn: { background: '#2a1515', border: '1px solid #5c2020', borderRadius: 7, padding: '6px 14px', color: '#ff6b6b', fontSize: 12, fontWeight: 500, cursor: 'pointer' },
  statusBadge: { fontSize: 11, fontWeight: 500, padding: '4px 10px', borderRadius: 6 },
  successMsg: { background: '#1a2e24', border: '1px solid #2a5a3a', borderRadius: 8, padding: '10px 14px', color: '#00d4a0', fontSize: 13, marginBottom: 16 },
};
